import axios from 'axios';

import {
  NewZoomWebinarRegistrant,
  ZoomRegistrantCreatedResponse,
  ZoomRegistrationSuccess,
  ZoomWebinar,
  ZoomWebinarOccurrence,
  ZoomWebinarRecurrence,
  ZoomWebinarRecurrenceType,
} from '../../types/zoom';

type GetWebinarResponse = {
  agenda: string
  duration: number
  id: number
  join_url: string
  occurrences?: OccurrencesResponse[]
  recurrence?: RecurrenceResponse
  start_time: string
  topic: string
  type: number
}

type OccurrencesResponse = {
  duration: number
  occurrence_id: string
  start_time: string
  status: string
}

type RecurrenceResponse = {
  end_date_time: string,
  monthly_day?: number,
  monthly_week?: number,
  monthly_week_day?: number,
  repeat_interval: number,
  type: number,
  weekly_days?: string
}

type ZoomRegistrantRequest = {
  company?: string,
  email: string,
  first_name: string,
  last_name: string
}

type RegisterProps = {
  occurrence?: string
  registrant: NewZoomWebinarRegistrant
  webinarId: number
}

const rootPath = `${process.env.GATSBY_MARKETING_BACKEND_BASE_URL}/zoom/webinars`;

const mapOccurrences = (occurrences: OccurrencesResponse[]): ZoomWebinarOccurrence[] => {
  return occurrences.map(occurrence => ({
    id: occurrence.occurrence_id,
    duration: occurrence.duration,
    startTime: occurrence.start_time,
    status: occurrence.status,
  }));
};

const mapRecurrence = (recurrence: RecurrenceResponse): ZoomWebinarRecurrence => {
  return {
    endDateTime: recurrence.end_date_time,
    monthlyDay: recurrence.monthly_day ?? undefined,
    monthlyWeek: recurrence.monthly_week ?? undefined,
    monthlyWeekDay: recurrence.monthly_week_day ?? undefined,
    repeatInterval: recurrence.repeat_interval,
    type: recurrenceTypeIntToEnumValue(recurrence.type),
    weeklyDays: recurrence.weekly_days ?? undefined,
  };
};

const recurrenceTypeIntToEnumValue = (type: number): ZoomWebinarRecurrenceType => {
  if (type === 1)
    return ZoomWebinarRecurrenceType.DAILY;
  else if (type === 2)
    return ZoomWebinarRecurrenceType.WEEKLY;
  else if (type === 3)
    return ZoomWebinarRecurrenceType.MONTHLY;
  else
    return ZoomWebinarRecurrenceType.MONTHLY;
};

class ZoomAPI {

  static async GetWebinar(webinarIdProp: number | string): Promise<ZoomWebinar> {

    let webinarId = 0;
    if (typeof webinarIdProp === 'string')
      webinarId = parseInt(webinarIdProp.trim().replace(/\s/g, ''));

    try {
      const { data } = await axios.get<GetWebinarResponse>(`${rootPath}/${webinarId}`);
      return {
        agenda: data.agenda === '' ? null : data.agenda,
        duration: data.duration,
        id: data.id,
        joinUrl: data.join_url,
        isRecurring: data.type === 9,
        occurrences: data.occurrences ? mapOccurrences(data.occurrences) : null,
        recurrence: data.type === 9 && data.recurrence ? mapRecurrence(data.recurrence) : null,
        startTime:data.start_time,
        topic: data.topic,
      };
    } catch (e) {
      return Promise.reject(e);
    }
  }

  static async RegisterForWebinar(props: RegisterProps): Promise<ZoomRegistrationSuccess> {

    const { occurrence, registrant, webinarId } = props;

    const reqData: ZoomRegistrantRequest = {
      email: registrant.email,
      first_name: registrant.firstName,
      last_name: registrant.lastName,
      company: registrant.companyName,
    };

    let url = `${rootPath}/${webinarId}/registrants`;
    
    if (!!occurrence && occurrence !== '')
      url = `${url}?occurrence=${occurrence}`;

    try {
      const { data } = await axios.post<ZoomRegistrantCreatedResponse>(url, reqData);
      return {
        registrant: props.registrant,
        response: {
          ...data,
          start_time: new Date(data.start_time),
        },
      };
    } catch (e) {
      return Promise.reject(e);
    }
  }

}

export default ZoomAPI;
